import { FormController } from "$root/third-party-integrations/react-hook-form";
import { valueByPath } from "$root/utils";
import type { StylableProps } from "@mdotm/shadcn/legacy/components";
import { type TextInputProps, TextInput, FormField, Label } from "@mdotm/shadcn/mdotm/components";
import { type NodeOrFn, renderNodeOrFn } from "@mdotm/shadcn/legacy/react-extensions";
import type { Control, FieldValues, FormState, Path } from "react-hook-form";

export type TextFieldProps<T extends FieldValues> = StylableProps & {
	control: Control<T, any>;
	formState: FormState<T>;
	name: Path<T>;
	label: NodeOrFn<{ htmlFor: string }>;
} & Omit<TextInputProps, "value">;

export function TextField<T extends FieldValues>({
	label,
	name,
	formState,
	control,
	className,
	style,
	...forward
}: TextFieldProps<T>): React.JSX.Element {
	return (
		<FormField
			className={className}
			style={style}
			label={({ htmlFor }) =>
				typeof label === "string" ? (
					<Label htmlFor={htmlFor} className="mb-px">
						{label}
					</Label>
				) : (
					renderNodeOrFn(label, { htmlFor })
				)
			}
			error={(valueByPath(formState.errors, name) as { message?: string })?.message}
		>
			{(fieldProps) => (
				<FormController
					control={control}
					name={name}
					render={({ field: { ref, onChange, ...controllerProps } }) => (
						<TextInput innerRef={ref} onChangeText={onChange} {...fieldProps} {...controllerProps} {...forward} />
					)}
				/>
			)}
		</FormField>
	);
}
